import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  ActivityIndicator,
  Modal,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Alert,
} from 'react-native';
import {
  MaterialCommunityIcons,
  Octicons,
  MaterialIcons,
  Ionicons,
  AntDesign,
} from '@expo/vector-icons';
import Feather from '@expo/vector-icons/Feather';
import { ContentIcon, Content } from './style';
import THEME from '../../config/theme';
import { FooterText, SmallText, Title } from '../../config/theme/globalStyles';
import { getDoc, setDoc, doc, getDocs, collection } from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import useViewport from "../../hooks/useViewport";
import { copyModule } from '../../utils/firebaseContentActions';

const ListHeader = ({
  cardId,
  categoryId,
  videoId,
  video,
  onEditCardInformationClick,
  onEditOrderClick,
  onDeleteCardClick,
  user,
  login,
  adminOptions,
}) => {

  const { customColor, customFont } = useCustomTheme();

  const [updatedPdf, setUpdatedPdf] = useState([]);

  const { width } = useViewport();
  const breakpoint = 1080;
  const Container = width < breakpoint ? View : ScrollView;

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);

  // Carrega as categorias disponíveis (supondo que elas estão na coleção "categories")
  const fetchCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(firestore, "categories"));
      const categoriesList = [];
      querySnapshot.forEach((docSnap) => {
        // Apenas inclua a categoria (você pode filtrar por status ou outras condições, se necessário)
        categoriesList.push({ id: docSnap.id, title: docSnap.data().title });
      });
      setAvailableCategories(categoriesList);
    } catch (error) {
      console.error("Erro ao buscar categorias: ", error);
    }
  };

  useEffect(() => {
    if (video?.pdf !== undefined) {
      if (Array.isArray(video?.pdf)) {
        setUpdatedPdf(video?.pdf);
      } else if (typeof video?.pdf === 'string' && video?.pdf.trim() === '') {
        setUpdatedPdf([]);
      } else if (typeof video?.pdf === 'string') {
        setUpdatedPdf([{ url: video?.pdf, name: '', title: video.pdf?.title || 'PDF' }]);
      } else {
        setUpdatedPdf([video.pdf]);
      }
    }
  }, [video?.pdf]);

  const [watched, setWatched] = useState(false);
  const [like, setLike] = useState(false);
  const [loadingWatchedUpdate, setLoadingWatchedUpdate] = useState(true);
  const [loadingLikeUpdate, setLoadingLikeUpdate] = useState(true);
  const [activeProgress, setActiveProgress] = useState(false);

  const intervalIdRef = useRef(null);
  const scriptTagRef = useRef(null);

  const videoUrl =
    video?.videoInfo?.storage == 'panda' ||
    video?.pandaVideoMetadata?.video_player;

  useEffect(() => {
    if (!user?.isAdmin && !scriptTagRef.current && videoUrl) {
      const scriptTag = document.createElement('script');
      scriptTag.src = 'https://player.pandavideo.com.br/api.v2.js';
      scriptTag.async = true;
      document.body.appendChild(scriptTag);
      scriptTagRef.current = scriptTag;

      scriptTag.onload = () => {
        window.pandascripttag = window.pandascripttag || [];
        window.pandascripttag.push(() => {
          const player = new PandaPlayer('panda-1', {
            onReady: () => {
              const intervalId = setInterval(() => {
                const progress =
                  (player.getCurrentTime() * 100) / player.getDuration();

                if (progress >= 95 && !watched) {
                  setWatched(true);
                  setActiveProgress(true);
                  handleUpdateWatched();
                }
              }, 500);

              // Store intervalId in the intervalIdRef
              intervalIdRef.current = intervalId;
            },
          });
        });
      };
    }

    return () => {
      const intervalId = intervalIdRef.current;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user?.isAdmin, watched]);

  const handleUpdateLike = useCallback(async () => {
    if (loadingLikeUpdate) {
      return;
    }
    setLoadingLikeUpdate(true);

    let date = new Date();

    try {
      const contentsDocRef = doc(
        firestore,
        'users',
        user?.id,
        'contents',
        videoId
      );
      await setDoc(
        contentsDocRef,
        {
          cardId,
          categoryId,
          markedAsFavoriteAt: date,
          isFavorite: !like,
        },
        { merge: true }
      );
      setLike(!like);
    } catch (error) {
      console.error('Error adding document isFavorite: ', error);
    }
    setLoadingLikeUpdate(false);
  }, [loadingLikeUpdate, user?.id, videoId, like]);

  const handleUpdateWatched = useCallback(async () => {
    if (loadingWatchedUpdate && watched) {
      return;
    } else if (activeProgress) {
      return;
    }
    setLoadingWatchedUpdate(true);

    let date = new Date();

    try {
      const contentsDocRef = doc(
        firestore,
        'users',
        user?.id,
        'contents',
        videoId
      );
      await setDoc(
        contentsDocRef,
        {
          cardId,
          categoryId,
          markedAsWatchedAt: date,
          isWatched: true,
        },
        { merge: true }
      );
      setWatched(true);
    } catch (error) {
      console.error('Error adding document isWatched: ', error);
    }
    setLoadingWatchedUpdate(false);
  }, [loadingWatchedUpdate, user?.id, videoId, watched]);

  const loadDetail = useCallback(async () => {
    try {
      const watchedDocRef = doc(
        firestore,
        'users',
        user?.id,
        'contents',
        videoId
      );
      const docSnap = await getDoc(watchedDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setWatched(data.isWatched);
        setLike(data.isFavorite);
      }
      setLoadingLikeUpdate(false);
      setLoadingWatchedUpdate(false);
    } catch (error) {
      console.log(error);
    }
  }, [user?.id, videoId]);

  useEffect(() => {
    loadDetail();
  }, []);

  const handlePress = (link) => {
    if (login) {
      window.open(link, '_blank');
    }
  };

  const Pdf = ({ pdfs }) => {
    const [modalVisible, setModalVisible] = useState(false);

    const openModal = () => {
      setModalVisible(true);
    };

    const closeModal = () => {
      setModalVisible(false);
    };

    const namePdf = (item) => {
      if (!item.title) {
        return item.name.slice(24);
      }

      if (item.name === item.title) {
        return item.name.slice(24);
      }
      return item.title;
    };

    return (
      <ContentIcon onPress={openModal}>
        <Ionicons
          name="document-text-outline"
          size={24}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
        />
        <FooterText
          fontSize={9}
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          margin="0.5rem 0rem"
        >
          Arquivo
        </FooterText>

        <Modal
          animationType="slide"
          transparent={true}
          visible={modalVisible}
          onRequestClose={closeModal}
        >
          <View
            style={{
              flex: 1,
              justifyContent: 'flex-start',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              paddingTop: '20%',
            }}
          >
            <View
              style={{
                width: '60%',
                backgroundColor: 'white',
                padding: 20,
                borderRadius: 10,
              }}
            >
              <Content justifyContent="space-between" padding="0.5rem">
                <SmallText
                  style={{
                    fontFamily:
                      customFont && customFont.fontFamilyText
                        ? customFont.fontFamilyText
                        : THEME.FONTFAMILY.REGULAR,
                  }}
                  color="black"
                >
                  Selecione um PDF:
                </SmallText>
                <TouchableOpacity onPress={closeModal}>
                  <AntDesign name="closecircleo" size={18} color="black" />
                </TouchableOpacity>
              </Content>
              <FlatList
                data={pdfs}
                keyExtractor={(item) => item?.url}
                renderItem={({ item }) => (
                  <View
                    style={{
                      padding: 10,
                      borderBottomWidth: 1,
                      borderBottomColor: '#ccc',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <TouchableOpacity
                      onPress={() => {
                        window.open(item.url, '_blank');
                      }}
                      style={{ paddingRight: '1.5rem' }}
                    >
                      <SmallText
                        style={{
                          fontFamily:
                            customFont && customFont.fontFamilyText
                              ? customFont.fontFamilyText
                              : THEME.FONTFAMILY.REGULAR,
                        }}
                        color="#2e2e2e"
                      >
                        {namePdf(item)}
                      </SmallText>
                    </TouchableOpacity>
                  </View>
                )}
              />
            </View>
          </View>
        </Modal>
      </ContentIcon>
    );
  };

  const Live = () => {
    return (
      <ContentIcon>
        <TouchableOpacity onPress={() => handlePress(video?.url)}>
          <Octicons
            name="link-external"
            size={24}
            color={
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN
            }
          />
          <FooterText
            fontSize={9}
            style={{
              fontFamily:
                customFont && customFont.fontFamilyText
                  ? customFont.fontFamilyText
                  : THEME.FONTFAMILY.LIGHT,
            }}
            color={
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN
            }
            margin="0.5rem 0rem"
          >
            Link
          </FooterText>
        </TouchableOpacity>
      </ContentIcon>
    );
  };

  const Favorite = () => {
    return (
      <ContentIcon disabled={loadingLikeUpdate} onPress={handleUpdateLike}>
        {loadingLikeUpdate ? (
          <ActivityIndicator
            color={
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN
            }
            size={24}
          />
        ) : (
          <Ionicons
            name={like ? 'ios-heart-sharp' : 'ios-heart-outline'}
            size={24}
            style={{
              color: customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN,
            }}
          />
        )}
        <FooterText
          fontSize={customFont && customFont.fontXS ? customFont.fontXS : 12}
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          margin="0.5rem 0rem"
        >
          {like ? 'Marcado como favorito' : 'Marcar como favorito'}
        </FooterText>
      </ContentIcon>
    );
  };

  const Watched = () => {
    return (
      <ContentIcon
        disabled={loadingWatchedUpdate || watched}
        onPress={handleUpdateWatched}
      >
        {loadingWatchedUpdate ? (
          <ActivityIndicator
            color={
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN
            }
            size={24}
          />
        ) : (
          <Ionicons
            name={
              watched ? 'md-checkmark-circle' : 'md-checkmark-circle-outline'
            }
            size={24}
            style={{
              color: customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN,
            }}
          />
        )}
        <FooterText
          fontSize={customFont && customFont.fontXS ? customFont.fontXS : 12}
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          margin="0.5rem 0rem"
        >
          {watched ? 'Conteúdo assistido' : 'Marcar como assistido'}
        </FooterText>
      </ContentIcon>
    );
  };

  // Componente para exibir a lista de categorias para o admin escolher
  const CategorySelectionModal = () => {
    return (
      <Modal
        animationType="slide"
        transparent={true}
        visible={showCategoryModal}
        onRequestClose={() => setShowCategoryModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ width: '80%', backgroundColor: 'white', borderRadius: 10, padding: 20 }}>
            <Title textAlign="center" margin="0 0 1rem 0" fontSize={16}>
              Selecione a Categoria de Destino
            </Title>
            <FlatList
              data={availableCategories}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={{ paddingVertical: 10, borderBottomWidth: 1, borderColor: '#ccc' }}
                  onPress={async () => {
                    try {
                      // Chama a função copyModule – ela deverá forçar archived:true na criação do novo módulo
                      await copyModule(categoryId, cardId, item.id);
                      Alert.alert("Sucesso", "Módulo copiado com sucesso!");
                      setShowCategoryModal(false);
                    } catch (error) {
                      Alert.alert("Erro", "Erro ao copiar módulo: " + error.message);
                    }
                  }}
                >
                  <FooterText>{item.title} (ID: {item.id})</FooterText>
                </TouchableOpacity>
              )}
            />
            <TouchableOpacity onPress={() => setShowCategoryModal(false)} style={{ marginTop: 20, alignSelf: 'center' }}>
              <FooterText color={THEME.COLORS.PRIMARY_900}>Cancelar</FooterText>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  };

  // Componente para o botão Copiar Módulo
  // const CopyModule = () => {
  //   const handleCopyModulePress = async () => {
  //     // Carrega as categorias disponíveis e exibe o modal
  //     await fetchCategories();
  //     setShowCategoryModal(true);
  //   };

  //   return (
  //     <>
  //       <ContentIcon
  //         onPress={handleCopyModulePress}
  //         padding="0.2rem"
  //       >
  //         <Feather
  //           name="copy"
  //           size={24}
  //           color={
  //             customColor && customColor.text && customColor.text.app
  //               ? customColor.text.app
  //               : THEME.COLORS.TEXT_MAIN
  //           }
  //         />
  //         <FooterText
  //           fontSize={9}
  //           style={{
  //             fontFamily: customFont && customFont.fontFamilyText ? customFont.fontFamilyText : THEME.FONTFAMILY.LIGHT,
  //           }}
  //           color={
  //             customColor && customColor.text && customColor.text.app
  //               ? customColor.text.app
  //               : THEME.COLORS.TEXT_MAIN
  //           }
  //           margin="0.5rem 0rem"
  //         >
  //           Copiar Módulo
  //         </FooterText>
  //       </ContentIcon>
  //       {showCategoryModal && <CategorySelectionModal />}
  //     </>
  //   );
  // };

  const Edit = () => {
    return (
      <ContentIcon
        onPress={onEditCardInformationClick}
        padding="0.2rem"
      >
        <MaterialCommunityIcons
          name="playlist-edit"
          size={26}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
        />
        <FooterText
          fontSize={9}
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          margin="0.5rem 0rem"
        >
          Editar Informações
        </FooterText>
      </ContentIcon>
    );
  };

  const DeleteCard = () => {
    return (
      <ContentIcon
        onPress={onDeleteCardClick}
        padding="0.2rem"
      >
        <MaterialIcons
          name="delete-outline"
          size={26}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
        />
        <FooterText
          fontSize={9}
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          margin="0.5rem 0rem"
        >
          Excluir Card
        </FooterText>
      </ContentIcon>
    );
  };

  const EditOrderVideos = () => {
    return (
      <ContentIcon
        onPress={onEditOrderClick}
        padding="0.2rem"
      >
        <Octicons
          name="list-ordered"
          size={24}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
        />
        <FooterText
          fontSize={9}
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          margin="0.5rem 0rem"
        >
          Alterar Ordem
        </FooterText>
      </ContentIcon>
    );
  };

  return (
    <Container>
      <Title
        fontSize={
          customFont && customFont.fontLX
            ? customFont.fontLX
            : THEME.FONTSIZE.BIG
        }
        style={{
          fontFamily:
            customFont && customFont.fontFamilyTitle
              ? customFont.fontFamilyTitle
              : THEME.FONTFAMILY.BOLD,
        }}
        color={
          customColor && customColor.text && customColor.text.app
            ? customColor.text.app
            : THEME.COLORS.TITLE_MAIN
        }
        textAlign="left"
      >
        {video?.title}
      </Title>
      {user?.isAdmin && adminOptions === true ? (
        <Content justifyContent="space-around" margin="1rem 0rem">
          <Edit />
          {/* <CopyModule />  */}
          <EditOrderVideos />
          <DeleteCard />
        </Content>
      ) : (
        <Content margin="1rem 0rem">
          {updatedPdf && updatedPdf.length > 0 && (
            <View style={{ width: '25%' }}>
              <Pdf pdfs={updatedPdf} />
            </View>
          )}
          {video?.url && (
            <View style={{ width: '25%' }}>
              <Live />
            </View>
          )}
          {user?.id && (
            <>
              <View style={{ width: '25%' }}>
                <Favorite />
              </View>
              <View style={{ width: '25%' }}>
                <Watched />
              </View>
            </>
          )}
        </Content>
      )}
      {video?.description?.length > 0 &&
        Array.isArray(video?.description) &&
        video?.description.map((i, index) => (
          <SmallText
            key={index}
            fontSize={
              customFont && customFont.fontSM
                ? customFont.fontSM
                : THEME.FONTSIZE.SMALL
            }
            style={{
              fontFamily:
                customFont && customFont.fontFamilyText
                  ? customFont.fontFamilyText
                  : THEME.FONTFAMILY.REGULAR,
            }}
            color={
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN
            }
            margin="0rem 0rem 1rem 0rem"
            textAlign="left"
          >
            {i}
          </SmallText>
        ))}
      {video?.description?.length > 0 && !Array.isArray(video?.description) && (
        <SmallText
          fontSize={
            customFont && customFont.fontSM
              ? customFont.fontSM
              : THEME.FONTSIZE.SMALL
          }
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.REGULAR,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          margin="0rem 0rem 1rem 0rem"
          textAlign="left"
        >
          {video?.description}
        </SmallText>
      )}
    </Container>
  );
};

export default ListHeader;
